import { Button, Card, Checkbox, Form, Input, Modal, Radio, Select } from 'antd';
import { useAppNavigate } from '@/hooks';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { useToast } from '@/widgets/common';

const { TextArea } = Input;

type ApplicationRegistrationFormValues = {
  identId: string;
  name: string;
  address: string;
  birthDate: string;
  householdNo: string;
  phone: string;
  email: string;
  applicationType: string;
  defect?: string;
  note?: string;
};

const requiredLabel = (label: string) => (
  <span className="inline-flex items-center gap-1">
    <span aria-hidden className="text-red-500 text-sm font-bold">*</span>
    <span>{label}</span>
  </span>
);

const ApplicationRegistration = () => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const [registrationForm] = Form.useForm<ApplicationRegistrationFormValues>();

  const register = async () => {
    try {
      await registrationForm.validateFields();
    } catch (validationError: unknown) {
      if (validationError && typeof validationError === 'object' && 'errorFields' in validationError) return;
      Modal.error({ content: '入力内容を確認してください。' });
      return;
    }
    toast.success('入力内容を確認しました。');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '申請書類登録' }]} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 gap-6">
          <Card title="申請者基本情報">
            <Form<ApplicationRegistrationFormValues>
              form={registrationForm}
              layout="vertical"
              requiredMark={false}
              className="customer-validation-form"
              validateTrigger="onSubmit"
              initialValues={{
                identId: '',
                name: '',
                address: '',
                birthDate: '',
                householdNo: '',
                phone: '',
                email: '',
                applicationType: '',
              }}
            >
              <Form.Item name="identId" label="識別ID">
                <Input placeholder="例: SID-U-001234" className="font-mono" />
              </Form.Item>
              <Form.Item
                name="name"
                label={requiredLabel('氏名')}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || value.trim().length === 0) {
                        throw new Error('氏名を入力してください。');
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="山田 太郎" />
              </Form.Item>
              <Form.Item
                name="address"
                label={requiredLabel('住所')}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || value.trim().length === 0) {
                        throw new Error('住所を入力してください。');
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="東京都千代田区..." />
              </Form.Item>
              <Form.Item
                name="birthDate"
                label={requiredLabel('生年月日')}
                rules={[
                  {
                    validator: async (_, value) => {
                      const v = String(value ?? '').trim();
                      if (!v) {
                        throw new Error('生年月日を入力してください。');
                      }
                      if (!/^\d{8}$/.test(v)) {
                        throw new Error('生年月日は西暦8桁（YYYYMMDD）で入力してください。');
                      }
                      const year = Number(v.slice(0, 4));
                      const month = Number(v.slice(4, 6));
                      const day = Number(v.slice(6, 8));
                      const date = new Date(year, month - 1, day);
                      const isRealDate =
                        date.getFullYear() === year &&
                        date.getMonth() === month - 1 &&
                        date.getDate() === day;
                      if (!isRealDate) {
                        throw new Error('生年月日が正しくありません。');
                      }
                      if (date.getTime() > Date.now()) {
                        throw new Error('生年月日に未来の日付は指定できません。');
                      }
                    },
                  },
                ]}
              >
                <Input
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="例: 19850401（西暦8桁）"
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 8);
                    registrationForm.setFieldValue('birthDate', digitsOnly);
                  }}
                />
              </Form.Item>
              <Form.Item
                name="householdNo"
                label={requiredLabel('被保護世帯番号')}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || value.trim().length === 0) {
                        throw new Error('被保護世帯番号を入力してください。');
                      }
                    },
                  },
                ]}
              >
                <Input placeholder="W-123456" />
              </Form.Item>
              <Form.Item name="phone" label="電話番号">
                <Input placeholder="03-1234-5678" />
              </Form.Item>
              <Form.Item
                name="email"
                label={requiredLabel('メールアドレス')}
                rules={[
                  {
                    validator: async (_, value) => {
                      const v = String(value ?? '').trim();
                      if (!v) {
                        throw new Error('メールアドレスを入力してください。');
                      }
                      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
                        throw new Error('メールアドレスの形式が正しくありません。');
                      }
                    },
                  },
                ]}
              >
                <Input type="email" placeholder="example@example.com" />
              </Form.Item>
              <Form.Item
                name="applicationType"
                label={requiredLabel('申請内容区分')}
                rules={[
                  {
                    validator: async (_, value) => {
                      if (!value || String(value).trim().length === 0) {
                        throw new Error('申請内容区分を選択してください。');
                      }
                    },
                  },
                ]}
              >
                <Select placeholder="選択してください">
                  <Select.Option value="追加給付申請">追加給付申請</Select.Option>
                  <Select.Option value="変更申請">変更申請</Select.Option>
                  <Select.Option value="再交付申請">再交付申請</Select.Option>
                </Select>
              </Form.Item>
            </Form>
          </Card>

          <Card title="添付書類チェック">
            <div className="space-y-6">
              <Card size="small" className="cursor-pointer">
                <Checkbox>
                  <div>
                    <p className="font-medium text-gray-800">本人確認書類</p>
                    <p className="text-xs text-gray-500">運転免許証、マイナンバーカード等</p>
                  </div>
                </Checkbox>
              </Card>
              <Card size="small" className="cursor-pointer">
                <Checkbox>
                  <div>
                    <p className="font-medium text-gray-800">申請書</p>
                    <p className="text-xs text-gray-500">所定の様式による申請書</p>
                  </div>
                </Checkbox>
              </Card>
              <Card size="small" className="cursor-pointer">
                <Checkbox>
                  <div>
                    <p className="font-medium text-gray-800">その他書類</p>
                    <p className="text-xs text-gray-500">通帳コピー、印鑑証明等</p>
                  </div>
                </Checkbox>
              </Card>

              <Form form={registrationForm} layout="vertical" className="pt-4 border-t border-gray-200">
                <Form.Item name="defect" label="不備有無">
                  <Radio.Group>
                    <div className="space-y-2">
                      <Radio value="none">なし</Radio>
                      <br />
                      <Radio value="exists">あり</Radio>
                    </div>
                  </Radio.Group>
                </Form.Item>
                <Form.Item name="note" label="備考">
                  <TextArea rows={4} placeholder="不備内容や特記事項を入力" />
                </Form.Item>
              </Form>
            </div>
          </Card>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button onClick={() => navigate('home')}>キャンセル</Button>
          <Button>保存して後で編集</Button>
          <Button type="primary" onClick={() => void register()}>登録する</Button>
        </div>
      </div>
    </div>
  );
};

export default ApplicationRegistration;
